import React from "react";
import { PageHeader } from "../components/Layout";
import { API } from "../lib/apiClient";
import { useI18n } from "../i18n/I18nContext";
import { FileCode2, Copy, KeyRound, Zap } from "lucide-react";
import { toast } from "sonner";

const METHOD_CLS = {
  GET: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30",
  POST: "bg-blue-500/15 text-blue-400 ring-blue-500/30",
  PUT: "bg-amber-500/15 text-amber-400 ring-amber-500/30",
  DELETE: "bg-rose-500/15 text-rose-400 ring-rose-500/30",
};

export default function ApiDocs() {
  const { t } = useI18n();
  const base = API;
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success(t("docs.copy") + " ✓"); };

  const endpoints = [
    { m: "POST", p: "/booking/oauth/token", d: "Ottieni un access token OAuth dalle credenziali del collegamento.",
      ex: `curl -X POST ${base}/booking/oauth/token \\\n  -H "Content-Type: application/json" \\\n  -d '{"client_id":"bkc_xxx","client_secret":"gws_xxx"}'` },
    { m: "GET", p: "/booking/structure", d: "Verifica il collegamento e recupera i dati della struttura autorizzata.", auth: true,
      ex: `curl ${base}/booking/structure \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>"` },
    { m: "POST", p: "/booking/reservations", d: "Crea l'accesso Wi-Fi per una prenotazione (genera un codice univoco, stato IN ATTESA).", auth: true,
      ex: `curl -X POST ${base}/booking/reservations \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"booking_reservation_id":"10025","guest_name":"Mario Rossi","accommodation":"Camera 12","checkin_date":"2026-09-15","checkout_date":"2026-09-18"}'` },
    { m: "PUT", p: "/booking/reservations/{id}", d: "Aggiorna una prenotazione mantenendo lo stesso codice Wi-Fi.", auth: true,
      ex: `curl -X PUT ${base}/booking/reservations/10025 \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"checkout_date":"2026-09-19"}'` },
    { m: "DELETE", p: "/booking/reservations/{id}", d: "Annulla la prenotazione e disabilita il codice Wi-Fi.", auth: true,
      ex: `curl -X DELETE ${base}/booking/reservations/10025 \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>"` },
    { m: "POST", p: "/booking/reservations/{id}/checkin", d: "Check-in: attiva il codice Wi-Fi (IN ATTESA → ATTIVO).", auth: true,
      ex: `curl -X POST ${base}/booking/reservations/10025/checkin \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>"` },
    { m: "POST", p: "/booking/reservations/{id}/checkout", d: "Check-out: disabilita il codice Wi-Fi (ATTIVO → DISABILITATO).", auth: true,
      ex: `curl -X POST ${base}/booking/reservations/10025/checkout \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>"` },
    { m: "GET", p: "/booking/reservations/{id}/code", d: "Recupera il codice Wi-Fi e il suo stato.", auth: true,
      ex: `curl ${base}/booking/reservations/10025/code \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>"` },
    { m: "POST", p: "/booking/webhook", d: "Riceve eventi in tempo reale: reservation.created, reservation.updated, reservation.cancelled, checkin, checkout. Idempotente tramite event_id.", auth: true,
      ex: `curl -X POST ${base}/booking/webhook \\\n  -H "Authorization: Bearer <ACCESS_TOKEN>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"event":"checkin","booking_reservation_id":"10025","event_id":"evt_abc123"}'` },
  ];

  return (
    <div data-testid="api-docs-page">
      <PageHeader title={t("docs.title")} subtitle={t("docs.subtitle")} />

      {/* Auth flow */}
      <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-9 w-9 rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20 flex items-center justify-center"><KeyRound className="h-4.5 w-4.5 text-amber-400" /></span>
          <h3 className="font-display font-semibold">{t("docs.flow_title")}</h3>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">{t("docs.flow_desc")}</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("docs.base_url")}</div>
            <div className="font-mono text-xs text-cyan-400 break-all">{base}</div>
          </div>
          <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("intg.token_url")}</div>
              <div className="font-mono text-xs text-cyan-400 truncate">{base}/booking/oauth/token</div>
            </div>
            <button data-testid="copy-token-url" onClick={() => copy(`${base}/booking/oauth/token`)} className="text-slate-400 hover:text-blue-400 shrink-0"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {/* Endpoints */}
      <div className="space-y-3">
        {endpoints.map((e, i) => (
          <div key={i} data-testid={`doc-endpoint-${i}`} className="rounded-xl border border-[#1F2937] bg-[#111827] p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold ring-1 ${METHOD_CLS[e.m]}`}>{e.m}</span>
              <code className="font-mono text-sm text-slate-100">{e.p}</code>
              {e.auth ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400"><KeyRound className="h-3 w-3" /> Bearer</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><Zap className="h-3 w-3" /> client credentials</span>
              )}
            </div>
            <p className="text-sm text-slate-400 mt-2">{e.d}</p>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{t("docs.example")}</span>
                <button data-testid={`copy-example-${i}`} onClick={() => copy(e.ex)} className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200"><Copy className="h-3.5 w-3.5" /> {t("docs.copy")}</button>
              </div>
              <pre className="rounded-lg bg-[#0B0F17] border border-[#1F2937] p-3 text-xs font-mono text-emerald-300 whitespace-pre-wrap overflow-x-auto">{e.ex}</pre>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
